import type { CmmdOrgProviderPolicy, ServerProvider } from "@cmmd-forge/contracts";
import { useMemo } from "react";

import type { ModelPickerProviderAvailability } from "../components/chat/modelPickerProviderAvailability";
import {
  railProviderSnapshots,
  resolveModelPickerRailProviders,
} from "../components/chat/modelPickerRail";
import type { ForgeProviderAccessState, ProviderCredentialMode } from "./forgeProviderAccess";
import type { AvailabilityProjection } from "./providerAvailabilityJoin";
import { resolveProviderUseVerdict } from "./providerAvailabilityJoin";

/**
 * Every input the join needs to answer "why can this person not pick this
 * provider here", for one Environment.
 *
 * `providers` is the Provider Inventory as published, never the eligible
 * subset: filtering before this point is what makes a Withheld Provider
 * vanish instead of carrying its reason. `hasLiveInventory` travels apart from
 * `providers` because an empty inventory and no inventory yet are different
 * facts — the join only reads a missing entry as Not on this Environment when
 * an Environment has actually published one.
 */
export interface ShellProviderAvailabilityInput {
  readonly providers: ReadonlyArray<ServerProvider>;
  readonly policy: CmmdOrgProviderPolicy | null | undefined;
  readonly access: ForgeProviderAccessState;
  readonly credentialMode: ProviderCredentialMode;
  readonly hasLiveInventory: boolean;
}

/**
 * One availability per rail row, for whichever surface supplies the inputs.
 *
 * The one producer. `resolveDraftProviderAvailability` used to be its own
 * copy of this fold; it now calls this instead, so a surface with CMMD
 * authority in scope — the new-thread Composer, an open thread, a task
 * launch, an automation — reaches the same answer for the same inputs. See
 * `docs/adr/withheld-providers-carry-their-reason.md`.
 *
 * `projection` names which verdict the rows answer. The default "pick" is the
 * display rule everywhere it applied before. A configuration surface passes
 * "run": what it configures has to execute, so its rows must name the Run
 * verdict's reason, including the transient readiness answer the Pick
 * projection deliberately discards (forge#3396). Both projections stay
 * projections of the join's one evaluation; nothing here recomputes one.
 */
export function resolveShellProviderAvailabilityRows(
  input: ShellProviderAvailabilityInput,
  projection: AvailabilityProjection = "pick",
): ReadonlyArray<ModelPickerProviderAvailability> {
  const snapshots = railProviderSnapshots(input.providers);
  return resolveModelPickerRailProviders({
    providers: input.providers,
    policy: input.policy,
  }).map((provider) => ({
    provider,
    availability: resolveProviderUseVerdict({
      provider,
      snapshot: snapshots.get(provider),
      access: input.access,
      credentialMode: input.credentialMode,
      hasLiveInventory: input.hasLiveInventory,
    })[projection],
  }));
}

/**
 * The join, memoized for a component that already holds every input.
 *
 * Call this where `useForgeProviderAccess` already runs rather than
 * recomputing the access state a second time: the orchestrator for an open
 * thread, the new-thread draft's execution target, a task launch, an
 * automation's provider selection. A surface with no Environment in scope has
 * nothing to call this with — it passes `PROVIDER_AVAILABILITY_PAYLOAD_ONLY`
 * instead.
 */
export function useShellProviderAvailability(
  input: ShellProviderAvailabilityInput,
  projection: AvailabilityProjection = "pick",
): ReadonlyArray<ModelPickerProviderAvailability> {
  const { providers, policy, access, credentialMode, hasLiveInventory } = input;
  return useMemo(
    () =>
      resolveShellProviderAvailabilityRows(
        {
          providers,
          policy,
          access,
          credentialMode,
          hasLiveInventory,
        },
        projection,
      ),
    [providers, policy, access, credentialMode, hasLiveInventory, projection],
  );
}
